import {
  Card,
  Text,
  InlineStack,
  BlockStack,
  Box,
  SkeletonBodyText,
} from "@medentic/polaris-react";
import { Patient, Property } from "../../types";
import { useTranslation } from "react-i18next";
import PatientCellRenderer from "../PatientCellRenderer";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import useTags from "@shared/hooks/common/useTags";

interface PropertiesCardProps {
  properties: Property[];
  patient?: Patient;
  loading?: boolean;
}

function PropertiesCard({ properties, patient, loading }: PropertiesCardProps) {
  const { t } = useTranslation();
  const { clinicId } = useParams<{ clinicId: string }>();
  const { tags } = useTags(clinicId ?? "");
  const tagsById = useMemo(
    () => new Map(tags.map((tag) => [tag.id, tag])),
    [tags]
  );

  const sortedProperties = properties
    .filter((property) => property.isShortlisted && patient?.[property.name])
    .sort((a, b) => {
      const orderA = a.displayOrder ?? Number.MAX_SAFE_INTEGER;
      const orderB = b.displayOrder ?? Number.MAX_SAFE_INTEGER;
      return orderA - orderB;
    });

  return (
    <Card>
      <Box paddingBlockEnd={"200"}>
        <Text as="h1" variant="headingMd">
          {t("patientDetails.properties")}
        </Text>
      </Box>
      {loading ? (
        <SkeletonBodyText lines={5} />
      ) : (
        <BlockStack gap="300">
          {sortedProperties.map((property) => (
            <InlineStack
              key={property.name}
              align="space-between"
              blockAlign="center"
            >
              <Text as="p" variant="bodyMd">
                {property.label}
              </Text>
              {patient && (
                <PatientCellRenderer
                  column={{
                    name: property.name,
                    label: property.label,
                    type: property.renderer?.type ?? "",
                    id: property.renderer?.id ?? "",
                    visible: true,
                    renderer: property.renderer,
                    options: property.options,
                  }}
                  patient={patient}
                  allowBadgeRefresh={true}
                  tagsById={tagsById}
                />
              )}
            </InlineStack>
          ))}
        </BlockStack>
      )}
    </Card>
  );
}

export default PropertiesCard;
